import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, Music, Download, Copy, Check } from 'lucide-react';
import { useMusicCart } from '../contexts/MusicCartContext';

const API = process.env.REACT_APP_BACKEND_URL;

const sanitizeTitle = (title) => {
  return (title || 'Track').replace(/[\\/:*?"<>|\r\n\t]+/g, '').trim() || 'Track';
};

const PurchasedTrackRow = ({ track }) => {
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(track.title || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      const node = document.getElementById(`success-title-${track.id}`);
      if (node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/music/tracks/${track.id}/download`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob',
        }
      );

      let filename = `IAMHETHATIS - ${sanitizeTitle(track.title)}.mp3`;
      const cd = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (cd) {
        const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
        if (utf8Match) {
          try { filename = decodeURIComponent(utf8Match[1]); } catch (_) { /* keep default */ }
        } else if (asciiMatch) {
          filename = asciiMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: response.data.type || 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      alert(`Download failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="flex items-center gap-3 p-3 bg-[#0D0D0D] border border-white/10 hover:border-[#D4AF37]/40 transition-colors"
      data-testid={`success-track-${track.id}`}
    >
      <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]">
        {track.cover_art_url && (
          <img src={track.cover_art_url} alt={track.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p
          id={`success-title-${track.id}`}
          className="text-sm font-medium truncate select-text cursor-text"
          style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
          data-testid={`success-track-title-${track.id}`}
        >
          {track.title}
        </p>
        {track.genre && (
          <p className="text-xs text-[#737373] uppercase tracking-wider">{track.genre}</p>
        )}
      </div>
      <button
        onClick={handleCopy}
        className="flex-shrink-0 p-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
        title={copied ? 'Copied!' : 'Copy title'}
        aria-label="Copy track title"
        data-testid={`success-copy-${track.id}`}
      >
        {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
      </button>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-[#D4AF37] text-black text-xs font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-60"
        data-testid={`success-download-${track.id}`}
      >
        {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        {downloading ? 'Downloading' : 'Download'}
      </button>
    </div>
  );
};

const MusicPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useMusicCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/api/payments/checkout/status/${sessionId}`);
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('success'); // Assume success if timeout
          clearCart();
        }
      } catch (e) {
        setStatus('error');
      }
    };

    pollStatus();
    // API and axios are stable constants - safe to omit
    // clearCart is from context and stable
  }, [sessionId, clearCart]);

  const purchasedTracks = paymentData?.purchased_tracks || [];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-16 flex items-start justify-center px-4" data-testid="music-purchase-success-page">
      <div className="max-w-xl w-full text-center">
        {status === 'loading' && (
          <div className="animate-fade-in-up">
            <Loader2 size={64} className="mx-auto text-[#D4AF37] animate-spin mb-6" />
            <h1 className="font-['Bebas_Neue'] text-3xl mb-4">PROCESSING PURCHASE</h1>
            <p className="text-[#A3A3A3]">Please wait while we unlock your music...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in-up">
            <div className="w-24 h-24 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-white" />
            </div>
            <h1 className="font-['Bebas_Neue'] text-4xl mb-4">PURCHASE COMPLETE!</h1>
            <p className="text-[#A3A3A3] mb-2">Your tracks have been unlocked.</p>
            {paymentData && paymentData.amount_total != null && (
              <p className="text-[#D4AF37] font-['Bebas_Neue'] text-2xl mb-6">
                ${(paymentData.amount_total / 100).toLocaleString()}
              </p>
            )}

            {purchasedTracks.length > 0 ? (
              <div className="bg-[#1A1A1A] border border-white/10 p-4 mb-8 text-left space-y-2" data-testid="purchased-tracks-list">
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 text-center">
                  Download your tracks
                </p>
                {purchasedTracks.map((t) => (
                  <PurchasedTrackRow key={t.id} track={t} />
                ))}
              </div>
            ) : (
              <div className="bg-[#1A1A1A] border border-white/10 p-6 mb-8">
                <Music size={32} className="mx-auto text-[#D4AF37] mb-4" />
                <p className="text-sm text-[#A3A3A3]">
                  Your purchased tracks are now available in full.
                  Go to your account to stream and download them anytime.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/account"
                className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
                data-testid="view-library-btn"
              >
                View My Library
              </Link>
              <Link
                to="/music"
                className="px-6 py-3 border border-white/20 text-white font-medium uppercase tracking-wider hover:border-[#D4AF37] transition-colors"
              >
                Discover More
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={48} className="text-white" />
            </div>
            <h1 className="font-['Bebas_Neue'] text-4xl mb-4">PURCHASE ISSUE</h1>
            <p className="text-[#A3A3A3] mb-8">
              There was an issue processing your purchase. Please try again or contact support.
            </p>
            <Link
              to="/music"
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors inline-block"
            >
              Return to Music
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicPurchaseSuccessPage;
